import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { audience } from '@bb/contracts';
import { Button } from '@bb/ui';
import type { AdminApi, FilterCatalog, FilterCatalogEntry } from '../../api/client.ts';
import { MessageTargetEditor } from './MessageTargetEditor.tsx';
import type { FilterLeaf } from './tree-edit.ts';

type Spec = Record<string, unknown> & { type: string; operator: string };

export const OPERATOR_LABEL: Record<string, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  matches_regex: 'matches regex',
  not_matches_regex: 'does not match regex',
  is_any_of: 'is any of',
  exists: 'is set',
  not_exists: 'is not set',
  gt: 'greater than',
  gte: 'at least',
  lt: 'less than',
  lte: 'at most',
  between: 'between',
  is_true: 'yes',
  is_false: 'no',
  before: 'before',
  after: 'after',
  on: 'on',
  more_than_days_ago: 'more than N days ago',
  less_than_days_ago: 'less than N days ago',
  within_next_days: 'within the next N days',
  includes_value: 'includes',
  not_includes_value: 'does not include',
  size_equals: 'has size',
  size_gt: 'has size greater than',
  size_lt: 'has size less than',
  in: 'is in',
  not_in: 'is not in',
  did: 'did',
  did_not: 'did not',
  more_than_ago: 'more than … ago',
  less_than_ago: 'less than … ago',
};

const ATTRIBUTION_FIELD_LABEL: Record<(typeof audience.INSTALL_ATTRIBUTION_FIELDS)[number], string> = { source: 'Source', source_kind: 'Source kind', campaign: 'Campaign', adgroup: 'Ad group', ad: 'Ad' };
const FIELD_LABEL: Record<audience.ProfileStringField, string> = {
  external_user_id: 'External user ID',
  email: 'Email',
  phone: 'Phone',
  first_name: 'First name',
  last_name: 'Last name',
  language: 'Language',
  country: 'Country',
  timezone: 'Time zone',
};

/** Used until the catalogue arrives (and in tests without it): the two core filter types of BB-40. */
export const CORE_CATALOG: FilterCatalog = {
  categories: [
    { key: 'custom_data', label: { uk: 'Custom data', en: 'Custom data' } },
    { key: 'identity', label: { uk: 'Ідентичність', en: 'Identity and testing' } },
  ],
  operatorsByType: audience.OPERATORS_BY_TYPE as unknown as Record<string, string[]>,
  types: [
    { type: 'custom_attribute', category: 'custom_data', label: { uk: 'Custom attribute', en: 'Custom attribute' }, valueType: 'string', operators: [...audience.STRING_OPERATORS], params: [{ name: 'key', kind: 'attribute', required: true }, { name: 'dataType', kind: 'data_type', required: true }], template: { type: 'custom_attribute', key: '', dataType: 'string', operator: 'equals', value: '' } },
    { type: 'profile_field', category: 'identity', label: { uk: 'Поле профілю', en: 'Profile field' }, valueType: 'string', operators: [...audience.STRING_OPERATORS], params: [{ name: 'field', kind: 'profile_field', required: true }], template: { type: 'profile_field', field: 'email', operator: 'equals', value: '' } },
  ],
};

const CHANNEL_LABEL: Record<(typeof audience.MESSAGE_CHANNELS)[number], string> = { push: 'Push', email: 'Email', sms: 'SMS', content_card: 'Content Card', in_app: 'In-app message', webhook: 'Webhook' };

const NO_VALUE = new Set(['exists', 'not_exists', 'is_true', 'is_false', 'in', 'not_in', 'did', 'did_not']);
const NUMERIC = new Set(['more_than_days_ago', 'less_than_days_ago', 'within_next_days', 'size_equals', 'size_gt', 'size_lt', 'more_than_ago', 'less_than_ago']);

/** Value type of a filter: the attribute's data type for custom attributes, otherwise the catalogue's. */
const valueTypeOf = (spec: Spec, entry: FilterCatalogEntry | undefined) => (spec.type === 'custom_attribute' ? (spec.dataType as string) : (entry?.valueType ?? 'string'));

/** A fresh operand when the operator changes, so a stale value never travels to an operator that reads it differently. */
function withOperator(spec: Spec, operator: string, valueType: string): Spec {
  const { value: _value, value2: _value2, ...rest } = spec;
  if (NO_VALUE.has(operator)) return { ...rest, operator } as Spec;
  if (valueType === 'push_states') return { ...rest, operator, value: ['opted_in'] } as Spec;
  if (valueType === 'group_state') return { ...rest, operator, value: 'subscribed' } as Spec;
  if (operator === 'between') return { ...rest, operator, value: 0, value2: 0 } as Spec;
  if (operator === 'is_any_of') return { ...rest, operator, value: [] } as Spec;
  if (operator === 'more_than_ago' || operator === 'less_than_ago') return { ...rest, operator, value: 1, unit: (spec.unit as string | undefined) ?? 'days' } as Spec;
  if (valueType === 'event_time') {
    const { unit: _unit, ...withoutUnit } = rest as Spec & { unit?: string };
    return { ...withoutUnit, operator, value: '' } as Spec;
  }
  if (valueType === 'number' || valueType === 'count' || NUMERIC.has(operator)) return { ...rest, operator, value: 0 } as Spec;
  return { ...rest, operator, value: '' } as Spec;
}

/** Operator and operand of a comparison of one value type (event property conditions reuse it). */
function ComparisonEditor({ label, dataType, cmp, operators, readOnly, onChange }: { label: string; dataType: string; cmp: Spec; operators: string[]; readOnly: boolean; onChange: (cmp: Spec) => void }) {
  const value = cmp.value;
  return (
    <>
      <select aria-label={`${label}: operator`} value={cmp.operator} disabled={readOnly} onChange={(e) => onChange(withOperator(cmp, e.target.value, dataType))}>
        {operators.map((op) => (
          <option key={op} value={op}>
            {OPERATOR_LABEL[op] ?? op}
          </option>
        ))}
      </select>
      {!NO_VALUE.has(cmp.operator) &&
        (dataType === 'number' || NUMERIC.has(cmp.operator) ? (
          <input aria-label={`${label}: value`} type="number" value={String(value ?? 0)} disabled={readOnly} onChange={(e) => onChange({ ...cmp, value: Number(e.target.value) })} />
        ) : (
          <input aria-label={`${label}: value`} value={typeof value === 'string' ? value : ''} disabled={readOnly} onChange={(e) => onChange({ ...cmp, value: e.target.value })} />
        ))}
    </>
  );
}

interface Props {
  node: FilterLeaf;
  index: number;
  count: number;
  readOnly: boolean;
  catalog: FilterCatalog;
  environmentId: string;
  onChange: (node: FilterLeaf) => void;
  onRemove: () => void;
  onMove: (delta: number) => void;
}

/** One condition, edited from the filter catalogue (BB-41): the type, its parameters, the operator and the operand. */
export function FilterRow({ node, index, count, readOnly, catalog, environmentId, onChange, onRemove, onMove }: Props) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const spec = node.filter as unknown as Spec;
  const entry = catalog.types.find((t) => t.type === spec.type);
  const valueType = valueTypeOf(spec, entry);
  const kinds = new Set(entry?.params.map((p) => p.kind));
  const apps = useQuery({ queryKey: ['apps', environmentId], queryFn: () => api.listApps(environmentId), enabled: kinds.has('app') });
  const groups = useQuery({ queryKey: ['subscription-groups', environmentId, 'audience'], queryFn: () => api.listSubscriptionGroups(environmentId, { status: 'active' }), enabled: kinds.has('subscription_group') });
  const events = useQuery({ queryKey: ['event-picker', environmentId], queryFn: () => api.eventPicker(environmentId, ''), enabled: kinds.has('event') });
  const chosenEvent = events.data?.find((e) => e.name === spec.event);
  const segmentable = (chosenEvent?.properties ?? []).filter((p) => p.segmentable);
  const set = (next: Spec) => onChange({ ...node, filter: next as never });
  const label = `Filter ${index + 1}`;
  const operator = spec.operator;
  const value = spec.value;
  const operators = spec.type === 'custom_attribute' ? (catalog.operatorsByType[valueType] ?? []) : (entry?.operators ?? []);
  const textValue = typeof value === 'string' ? value : '';

  const param = (p: FilterCatalogEntry['params'][number]) => {
    const current = spec[p.name];
    const update = (next: unknown) => set({ ...spec, [p.name]: next === '' || next === undefined ? undefined : next } as Spec);
    const aria = `${label}: ${p.kind === 'app' ? 'app' : p.kind === 'subscription_group' ? 'group' : p.kind === 'data_type' ? 'type' : p.kind === 'profile_field' || p.kind === 'attribution_field' ? 'field' : p.kind === 'device_scope' ? 'devices' : p.name === 'key' ? 'attribute' : p.name}`;
    switch (p.kind) {
      case 'attribute':
        return <input key={p.name} aria-label={aria} value={String(current ?? '')} disabled={readOnly} placeholder="deposits_count" onChange={(e) => set({ ...spec, key: e.target.value })} />;
      case 'data_type':
        return (
          <select key={p.name} aria-label={aria} value={String(current)} disabled={readOnly} onChange={(e) => set(withOperator({ ...spec, dataType: e.target.value }, 'exists', e.target.value))}>
            {audience.FilterValueType.options.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        );
      case 'profile_field':
        return (
          <select key={p.name} aria-label={aria} value={String(current)} disabled={readOnly} onChange={(e) => update(e.target.value)}>
            {audience.PROFILE_STRING_FIELDS.map((f) => (
              <option key={f} value={f}>
                {FIELD_LABEL[f]}
              </option>
            ))}
          </select>
        );
      case 'attribution_field':
        return (
          <select key={p.name} aria-label={aria} value={String(current)} disabled={readOnly} onChange={(e) => update(e.target.value)}>
            {audience.INSTALL_ATTRIBUTION_FIELDS.map((f) => (
              <option key={f} value={f}>
                {ATTRIBUTION_FIELD_LABEL[f]}
              </option>
            ))}
          </select>
        );
      case 'app':
        return (
          <select key={p.name} aria-label={aria} value={String(current ?? '')} disabled={readOnly} onChange={(e) => update(e.target.value)}>
            {!p.required && <option value="">Any app</option>}
            {p.required && !current && <option value="">Choose an app</option>}
            {(apps.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        );
      case 'subscription_group':
        return (
          <select key={p.name} aria-label={aria} value={String(current ?? '')} disabled={readOnly} onChange={(e) => update(e.target.value)}>
            {!current && <option value="">Choose a group</option>}
            {(groups.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        );
      case 'days':
        return <input key={p.name} aria-label={aria} type="number" min={1} max={365} value={String(current ?? 30)} disabled={readOnly} onChange={(e) => update(Number(e.target.value))} />;
      case 'platform':
        return (
          <select key={p.name} aria-label={`${label}: platform`} value={String(current ?? '')} disabled={readOnly} onChange={(e) => update(e.target.value)}>
            <option value="">Any platform</option>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
            <option value="web">Web</option>
          </select>
        );
      case 'device_scope':
        return (
          <select key={p.name} aria-label={aria} value={String(current ?? 'any_device')} disabled={readOnly} onChange={(e) => update(e.target.value)}>
            <option value="any_device">Send to all devices</option>
            <option value="matching_devices_only">Send to matching devices only</option>
          </select>
        );
      case 'segment':
        return <input key={p.name} aria-label={`${label}: segment`} value={String(current ?? '')} disabled={readOnly} placeholder="segment id" onChange={(e) => update(e.target.value)} />;
      case 'event':
        return (
          <select key={p.name} aria-label={`${label}: event`} value={String(current ?? '')} disabled={readOnly} onChange={(e) => set({ ...spec, event: e.target.value, ...(spec.type === 'event_property_in_window' ? { property: { path: '', dataType: 'string', operator: 'equals', value: '' } } : {}) } as Spec)}>
            {!current && <option value="">Choose an event</option>}
            {(events.data ?? []).map((ev) => (
              <option key={ev.name} value={ev.name}>
                {ev.name}
                {ev.deprecated ? ' (deprecated)' : ''}
              </option>
            ))}
          </select>
        );
      case 'time_unit':
        return operator === 'more_than_ago' || operator === 'less_than_ago' ? (
          <select key={p.name} aria-label={`${label}: unit`} value={String(current ?? 'days')} disabled={readOnly} onChange={(e) => update(e.target.value)}>
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
        ) : null;
      case 'event_window': {
        const window = (current as { days: number; mode: 'rolling' | 'calendar' } | undefined) ?? { days: 30, mode: 'calendar' };
        return (
          <span key={p.name} role="group" aria-label={`${label}: window`}>
            <input aria-label={`${label}: window days`} type="number" min={1} value={String(window.days)} disabled={readOnly} onChange={(e) => update({ ...window, days: Number(e.target.value) })} />
            <select aria-label={`${label}: window mode`} value={window.mode} disabled={readOnly} aria-describedby={`${node.id}-window-hint`} onChange={(e) => update({ ...window, mode: e.target.value })}>
              <option value="calendar">calendar days</option>
              <option value="rolling">last N × 24 hours</option>
            </select>
            <span id={`${node.id}-window-hint`} className="hint">
              {window.mode === 'calendar'
                ? `Today and the ${window.days - 1} days before it in the environment time zone.`
                : `Exactly the last ${window.days * 24} hours; limited by the environment lookback.`}
            </span>
          </span>
        );
      }
      case 'message_target':
      case 'campaign_or_canvas':
        return (
          <MessageTargetEditor
            key={p.name}
            label={label}
            value={current as { kind: audience.MessageTarget['kind']; id: string } | undefined}
            kinds={p.kind === 'message_target' ? ['campaign', 'canvas', 'canvas_step', 'tag'] : ['campaign', 'canvas']}
            environmentId={environmentId}
            readOnly={readOnly}
            onChange={update}
          />
        );
      case 'within_days':
        return (
          <input
            key={p.name}
            aria-label={`${label}: within days`}
            type="number"
            min={1}
            max={365}
            value={typeof current === 'number' ? String(current) : ''}
            placeholder="any time"
            disabled={readOnly}
            onChange={(e) => update(e.target.value === '' ? undefined : Number(e.target.value))}
          />
        );
      case 'canvas':
        return <input key={p.name} aria-label={`${label}: Canvas id`} value={String(current ?? '')} disabled={readOnly} spellCheck={false} placeholder="ID from its page" onChange={(e) => update(e.target.value.trim())} />;
      case 'variant':
        return <input key={p.name} aria-label={`${label}: variant`} value={String(current ?? '')} disabled={readOnly} placeholder="any variant" onChange={(e) => update(e.target.value.trim())} />;
      case 'channel':
        return (
          <select key={p.name} aria-label={`${label}: channel`} value={String(current ?? '')} disabled={readOnly} onChange={(e) => update(e.target.value)}>
            <option value="">Any channel</option>
            {audience.MESSAGE_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABEL[c]}
              </option>
            ))}
          </select>
        );
      case 'event_property': {
        const property = (current as Spec & { path: string; dataType: string }) ?? { path: '', dataType: 'string', operator: 'equals', value: '' };
        return (
          <span key={p.name} role="group" aria-label={`${label}: property`}>
            <select
              aria-label={`${label}: property path`}
              value={property.path}
              disabled={readOnly}
              onChange={(e) => {
                const chosen = segmentable.find((x) => x.path === e.target.value);
                update(chosen ? withOperator({ ...property, path: chosen.path, dataType: chosen.dataType } as Spec, 'equals', chosen.dataType) : property);
              }}
            >
              {!property.path && <option value="">Choose a property</option>}
              {segmentable.map((x) => (
                <option key={x.path} value={x.path}>
                  {x.path} ({x.dataType})
                </option>
              ))}
            </select>
            {chosenEvent && segmentable.length === 0 && <span className="hint">No segmentable properties: mark them on the event page.</span>}
            {property.path && (
              <ComparisonEditor label={`${label}: property`} dataType={property.dataType} cmp={property} operators={catalog.operatorsByType[property.dataType] ?? []} readOnly={readOnly} onChange={(next) => update(next)} />
            )}
          </span>
        );
      }
    }
  };

  const operand = () => {
    if (operators.length === 0 || NO_VALUE.has(operator)) return null;
    if (valueType === 'push_states') {
      const states = Array.isArray(value) ? (value as string[]) : [];
      return (
        <span role="group" aria-label={`${label}: states`}>
          {audience.PUSH_SUBSCRIPTION_STATES.map((state) => (
            <label key={state}>
              <input
                type="checkbox"
                checked={states.includes(state)}
                disabled={readOnly}
                onChange={(e) => set({ ...spec, value: e.target.checked ? [...states, state] : states.filter((s) => s !== state) })}
              />{' '}
              {state}
            </label>
          ))}
        </span>
      );
    }
    if (valueType === 'group_state') {
      return (
        <select aria-label={`${label}: value`} value={textValue} disabled={readOnly} onChange={(e) => set({ ...spec, value: e.target.value })}>
          <option value="subscribed">subscribed</option>
          <option value="unsubscribed">unsubscribed</option>
        </select>
      );
    }
    if (operator === 'is_any_of') {
      return (
        <input
          aria-label={`${label}: values`}
          value={Array.isArray(value) ? value.join(', ') : ''}
          disabled={readOnly}
          placeholder="UA, PL"
          onChange={(e) =>
            set({
              ...spec,
              value: e.target.value
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean),
            })
          }
        />
      );
    }
    if (operator === 'between') {
      return (
        <>
          <input aria-label={`${label}: from`} type="number" value={String(value ?? 0)} disabled={readOnly} onChange={(e) => set({ ...spec, value: Number(e.target.value) })} />
          <input aria-label={`${label}: to`} type="number" value={String(spec.value2 ?? 0)} disabled={readOnly} onChange={(e) => set({ ...spec, value2: Number(e.target.value) })} />
        </>
      );
    }
    if (valueType === 'number' || valueType === 'count' || NUMERIC.has(operator)) {
      return <input aria-label={`${label}: value`} type="number" step="any" value={String(value ?? 0)} disabled={readOnly} onChange={(e) => set({ ...spec, value: Number(e.target.value) })} />;
    }
    const inputType = operator === 'on' || operator === 'before' || operator === 'after' ? 'date' : 'text';
    return <input aria-label={`${label}: value`} type={inputType} value={textValue} disabled={readOnly} placeholder={valueType === 'version' ? '5.10.0' : undefined} onChange={(e) => set({ ...spec, value: e.target.value })} />;
  };

  return (
    <li className="audience-filter" aria-label={label} data-node-id={node.id}>
      <select aria-label={`${label}: source`} value={spec.type} disabled={readOnly} onChange={(e) => set({ ...(catalog.types.find((t) => t.type === e.target.value)?.template ?? { type: e.target.value }) } as Spec)}>
        {catalog.categories.map((category) => (
          <optgroup key={category.key} label={category.label.en}>
            {catalog.types
              .filter((t) => t.category === category.key)
              .map((t) => (
                <option key={t.type} value={t.type}>
                  {t.label.en}
                </option>
              ))}
          </optgroup>
        ))}
      </select>
      {entry?.params.map(param)}
      {operators.length > 0 && (
        <select aria-label={`${label}: operator`} value={operator} disabled={readOnly} onChange={(e) => set(withOperator(spec, e.target.value, valueType))}>
          {operators.map((op) => (
            <option key={op} value={op}>
              {OPERATOR_LABEL[op] ?? op}
            </option>
          ))}
        </select>
      )}
      {operand()}
      {!readOnly && (
        <span className="audience-filter__actions">
          <Button size="sm" variant="ghost" onClick={() => onMove(-1)} disabled={index === 0} aria-label={`Move ${label.toLowerCase()} up`}>
            ↑
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onMove(1)} disabled={index === count - 1} aria-label={`Move ${label.toLowerCase()} down`}>
            ↓
          </Button>
          <Button size="sm" variant="ghost" onClick={onRemove} aria-label={`Remove ${label.toLowerCase()}`}>
            Remove
          </Button>
        </span>
      )}
    </li>
  );
}
